import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Play, Wrench } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { ConfirmDialog } from '@/components/app/ConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient, humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';

type Adr004Scope =
  | 'all'
  | 'accessoryOwnership'
  | 'defaultAccessories'
  | 'userPoints';

const ADR004_SCOPES: { value: Adr004Scope; label: string; description: string }[] = [
  {
    value: 'all',
    label: 'All migrations',
    description: 'Run every sub-migration in sequence. Safe to re-run — each step is idempotent.',
  },
  {
    value: 'accessoryOwnership',
    label: 'Accessory ownership',
    description: 'Backfill ownership docs from user.ownedAccessoryIds.',
  },
  {
    value: 'defaultAccessories',
    label: 'Default accessories',
    description: 'Grant DEFAULT_FREE_ACCESSORIES to all existing users.',
  },
  {
    value: 'userPoints',
    label: 'User points',
    description: 'Reconcile userPoints docs with the progression event log.',
  },
];

export function MigrationsPage() {
  const [scope, setScope] = React.useState<Adr004Scope>('all');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [lastResult, setLastResult] = React.useState<{ scope: Adr004Scope; payload: unknown } | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      apiClient.post<unknown>('/migrations/adr004', { migrationName: scope }),
    onSuccess: (payload) => {
      setLastResult({ scope, payload });
      toast({ title: 'Migration completed', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Migration failed', description: humanizeError(err), variant: 'destructive' }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Migrations"
        description="One-shot data migrations. All operations are idempotent by design — safe to re-run."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wrench className="h-4 w-4" /> ADR-004 migrations
          </CardTitle>
          <CardDescription>
            Store/progression backfills introduced by ADR-004. Re-running a completed scope
            is a no-op.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Select value={scope} onValueChange={(v) => setScope(v as Adr004Scope)}>
              <SelectTrigger className="w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADR004_SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setConfirmOpen(true)} disabled={mut.isPending}>
              <Play className="mr-1 h-4 w-4" />
              {mut.isPending ? 'Running…' : 'Run'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {ADR004_SCOPES.find((s) => s.value === scope)?.description}
          </p>
          {lastResult && (
            <div className="space-y-1 pt-2">
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="success">Last run</Badge>
                <span>{lastResult.scope}</span>
              </div>
              <pre className="overflow-auto rounded bg-muted/30 p-3 font-mono text-xs">
                {JSON.stringify(lastResult.payload, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      {confirmOpen && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setConfirmOpen(false)}
          title={`Run migration: ${scope}?`}
          description="Writes data migration results to Firestore. Idempotent, but slow to revert. Prefer running in a non-peak window."
          destructive
          typedConfirmation="run"
          confirmLabel="Run migration"
          submitting={mut.isPending}
          onConfirm={async () => {
            await mut.mutateAsync();
            setConfirmOpen(false);
          }}
        />
      )}
    </div>
  );
}
